// Off-React store + pure analysis for audio reactivity.
//
// Audio levels update ~60x/sec. Per the project's hard-won rule (see the show
// buffers) we NEVER push that through React state — the live analyser and the
// computed band values live here, and the in-Canvas `AudioSampler` reads the
// analyser + writes the values each frame; the shaders/scene read the values.

export type AudioValues = {
  level: number; // overall energy 0..1 (smoothed)
  bass: number; // low band 0..1
  mid: number; // mid band 0..1
  treble: number; // high band 0..1
  beat: number; // 0..1 spike on a kick, decays fast
};

type Bus = {
  analyser: AnalyserNode | null;
  freq: Uint8Array | null;
  wave: Uint8Array | null; // time-domain samples (for waveform visualizers)
  sampleRate: number;
  gain: number; // user "Intensity" multiplier
  values: AudioValues;
  bassAvg: number; // running bass average, for beat detection
  beatCooldown: number; // refractory timer (s)
  replayFeed: boolean; // a flight-tape replay is writing values — the sampler stands down
};

export const audioBus: Bus = {
  analyser: null,
  freq: null,
  wave: null,
  sampleRate: 48000,
  gain: 1,
  values: { level: 0, bass: 0, mid: 0, treble: 0, beat: 0 },
  bassAvg: 0,
  beatCooldown: 0,
  replayFeed: false,
};

export function attachAnalyser(analyser: AnalyserNode, sampleRate: number) {
  audioBus.analyser = analyser;
  audioBus.freq = new Uint8Array(analyser.frequencyBinCount);
  audioBus.wave = new Uint8Array(analyser.fftSize);
  audioBus.sampleRate = sampleRate;
  audioBus.bassAvg = 0;
  audioBus.beatCooldown = 0;
}

export function detachAnalyser() {
  audioBus.analyser = null;
  audioBus.freq = null;
  audioBus.wave = null;
  // values are left to decay to rest inside the sampler
}

/**
 * Average a byte-frequency slice over [loHz, hiHz) and normalise to 0..1. Pure —
 * unit-tested headless with synthetic spectra (bin 0 = DC is skipped).
 */
export function bandEnergy(freq: Uint8Array, sampleRate: number, loHz: number, hiHz: number): number {
  const binHz = sampleRate / (freq.length * 2); // freq.length = fftSize / 2
  const lo = Math.max(1, Math.floor(loHz / binHz));
  const hi = Math.min(freq.length, Math.ceil(hiHz / binHz));
  if (hi <= lo) return 0;
  let sum = 0;
  for (let i = lo; i < hi; i++) sum += freq[i];
  return sum / (hi - lo) / 255;
}

export type Bands = { level: number; bass: number; mid: number; treble: number };

/** Instant (unsmoothed) band energies from a byte-frequency array. Pure. */
export function computeBands(freq: Uint8Array, sampleRate: number): Bands {
  return {
    bass: bandEnergy(freq, sampleRate, 20, 200),
    mid: bandEnergy(freq, sampleRate, 200, 2000),
    treble: bandEnergy(freq, sampleRate, 2000, 12000),
    level: bandEnergy(freq, sampleRate, 20, 12000),
  };
}
